import createSelector from 'selectorator'
import { identity, pathOr } from 'ramda'
import { isValid } from 'date-fns'
import { RootState } from 'src/state/types'
import { dateUtils } from '../../../utils'
import { userApiSelectors } from '../user'
import { NAMESPACE } from './constants'
import * as utils from './utils'
import {
  TApplication,
  TCommission,
  TInvoiceSchedule,
  TInvoiceTemplate,
  TPortfolio,
  TPortfolioPartyGroup,
  TPortfolioState,
  TMaybeId,
  TPortfolioStatus,
  TPortfolioSummary,
} from './types'

/**
 * Reads an arbitrary path off a portfolio or summary. The path is dynamic, so the result
 * genuinely cannot be typed from it — this `any` is a deliberate escape hatch, and the
 * named selectors below (which read `TPortfolio` directly) should be preferred.
 */
type TFieldReader = (id: TMaybeId, path: (string | number)[], defaultValue?: any) => any

/**
 * Get a slice of the portfolios API state
 * EG: getSlice('byId')
 */
const getSlice = <K extends keyof TPortfolioState>(key: K) =>
  createSelector<RootState, TPortfolioState[K]>([`api.${NAMESPACE}.${key}`], identity)

/**
 * Get a list of portfolio IDs
 */
export const getPortfolioIds = getSlice('allIds')

const getPortfoliosById = getSlice('byId')

/**
 * Get a list of portfolio objects
 * Correctly orders portfolios based on API response using `allIds` index
 */
export const getPortfolios = createSelector<RootState, TPortfolio[]>(
  [getPortfoliosById, getPortfolioIds],
  (byId: TPortfolioState['byId'], allIds: string[]) => allIds.map(id => byId[id]).filter(Boolean),
)

/**
 * Get a list of portfolios with a specific property ID
 */
export const getPortfoliosByPropertyId = createSelector<RootState, (propertyId: TMaybeId) => TPortfolio[]>(
  [getPortfolios],
  (portfolios: TPortfolio[]) => (propertyId: TMaybeId) => portfolios.filter(p => p.propertyId === propertyId),
)

/**
 * Get a portfolio object by ID
 */
export const getPortfolioById = createSelector<RootState, (id: TMaybeId) => TPortfolio | undefined>(
  [getPortfoliosById],
  (portfolios: TPortfolioState['byId']) => (id: TMaybeId) => id ? portfolios[id] : undefined,
)

/**
 * Read an arbitrary path off a portfolio.
 * EG: `getPortfolioFieldById(state)(id, ['landlords', 0, 'partyId'])`
 *
 * The path is dynamic, so the result cannot be typed from it — callers that want a real
 * type should pass a `defaultValue` (which fixes `T`) or use one of the named selectors
 * below, which all read through {@link getPortfolioById} directly.
 */
export const getPortfolioFieldById = createSelector<RootState, TFieldReader>(
  [identity],
  (state: RootState) => (id: TMaybeId, path: (string | number)[], defaultValue?: unknown) =>
    pathOr(defaultValue, path, getPortfolioById(state)(id)),
)

/**
 * Get a portfolio monthly rent amount, in cents
 */
export const getMonthlyRentAmount = createSelector<RootState, (id: TMaybeId) => number>(
  [getPortfolioById],
  (byId: (id: TMaybeId) => TPortfolio | undefined) => (id: TMaybeId) =>
    byId(id)?.contractContainer?.value.terms.monthlyRentAmount ?? 0,
)

/**
 * Get a portfolio's propertyId
 */
export const getPropertyIdByPortfolioId = createSelector<RootState, (id: TMaybeId) => string | undefined>(
  [getPortfolioById],
  (byId: (id: TMaybeId) => TPortfolio | undefined) => (id: TMaybeId) => byId(id)?.propertyId,
)

/**
 * Get a portfolio's landlord and tenant `partyId`s
 */
export const selectLandordAndTenantIdsFromPortfolio = createSelector<RootState, (id: TMaybeId) => string[]>(
  [getPortfolioById],
  (byId: (id: TMaybeId) => TPortfolio | undefined) => (id: TMaybeId) =>
    utils.getTenantAndLandlordIdsFromPortfolio(byId(id)),
)

/**
 * Get invoice schedules slice
 */
export const getInvoiceSchedules = getSlice('invoiceSchedules')

/**
 * Get invoice schedules for a portfolio
 */
export const getInvoiceSchedulesByPortfolioId = createSelector<RootState, (id: TMaybeId) => TInvoiceSchedule[]>(
  [getInvoiceSchedules],
  (schedules: TPortfolioState['invoiceSchedules']) => (id: TMaybeId) => (id ? schedules[id] : undefined) ?? [],
)

/**
 * Applications
 */

const getApplications = getSlice('applications')

/**
 * Get a portfolio's applications in portfolio.applications
 */
export const getApplicationsByPortfolioId = createSelector<RootState, (id: TMaybeId) => TApplication[]>(
  [getApplications],
  (applications: TPortfolioState['applications']) => (id: TMaybeId) => (id ? applications?.[id] : undefined) || [],
)

/**
 * Get a portfolio's accepted application in portfolio.applications.parties
 */
export const getAcceptedApplicationByPortfolioId = createSelector<
  RootState,
  (id: TMaybeId) => TApplication | undefined
>(
  [getApplicationsByPortfolioId],
  (byId: (id: TMaybeId) => TApplication[]) => (id: TMaybeId) => byId(id).find(app => app.status === 'Accepted'),
)

export const getApplicationByPartyId = createSelector<
  RootState,
  (id: TMaybeId, partyId: string) => TApplication | undefined
>(
  [getApplicationsByPortfolioId],
  (byId: (id: TMaybeId) => TApplication[]) => (id: TMaybeId, partyId: string) =>
    byId(id).find(app => app.partyId === partyId),
)

/**
 * Get all a portfolio's applications by portfolio ID
 *
 * @deprecated identical to {@link getApplicationsByPortfolioId}
 */
export const getAllApplicationsByPortfolioId = getApplicationsByPortfolioId

/**
 * Get a portfolio's owners in portfolio.parties.owners
 */
export const getOwnersByPortfolioId = createSelector<RootState, (id: TMaybeId) => TPortfolioPartyGroup>(
  [getPortfolioById],
  (byId: (id: TMaybeId) => TPortfolio | undefined) => (id: TMaybeId) => byId(id)?.parties?.owners ?? {},
)

/**
 * Get all a portfolio's landlord IDs by portfolio ID
 */
export const getAllOwnerPartyIdsByPortfolioId = createSelector<RootState, (id: TMaybeId) => string[]>(
  [getOwnersByPortfolioId],
  (byId: (id: TMaybeId) => TPortfolioPartyGroup) => (id: TMaybeId) => utils.getAllPartyIds(byId(id)),
)

/**
 * Get portfolio's primary landlord by portfolio ID
 */
export const getPrimaryLandlordByPortfolioId = createSelector<RootState, (id: TMaybeId) => string | null>(
  [getOwnersByPortfolioId],
  (byId: (id: TMaybeId) => TPortfolioPartyGroup) => (id: TMaybeId) => byId(id)?.primary?.partyId ?? null,
)

/**
 * Get a portfolio's tenants in portfolio.parties.tenants
 */
export const getTenantsByPortfolioId = createSelector<RootState, (id: TMaybeId) => TPortfolioPartyGroup>(
  [getPortfolioById],
  (byId: (id: TMaybeId) => TPortfolio | undefined) => (id: TMaybeId) => byId(id)?.parties?.tenants ?? {},
)

/**
 * Get a portfolio's tenant `partyId` by portfolio ID
 *
 * @deprecated use getPrimaryTenantByPortfolioId
 */
export const getTenantByPortfolioId = createSelector<RootState, (id: TMaybeId) => string | null>(
  [getTenantsByPortfolioId],
  (byId: (id: TMaybeId) => TPortfolioPartyGroup) => (id: TMaybeId) => byId(id)?.primary?.partyId ?? null,
)

/**
 * Get all a portfolio's tenant IDs by portfolio ID
 */
export const getAllTenantPartyIdsByPortfolioId = createSelector<RootState, (id: TMaybeId) => string[]>(
  [getTenantsByPortfolioId],
  (byId: (id: TMaybeId) => TPortfolioPartyGroup) => (id: TMaybeId) => utils.getAllPartyIds(byId(id)),
)

/**
 * Get all a portfolio's commission split agent IDs by portfolio ID
 */
export const getAllCommissionSplitAgentIdsByPortfolioId = createSelector<RootState, (id: TMaybeId) => string[]>(
  [getPortfolioById],
  (byId: (id: TMaybeId) => TPortfolio | undefined) => (id: TMaybeId) => {
    // Managed leases carry their agents on the management fee; unmanaged leases have no
    // management fee and carry them on the procurement fee. Union both (deduped) so the
    // selector returns every commission-split agent regardless of lease type.
    const commission = byId(id)?.commission
    const managementSplits = commission?.managementFee?.splits ?? []
    const procurementSplits = commission?.procurementFee?.splits ?? []
    return Array.from(new Set([...utils.getAllAgentIds(managementSplits), ...utils.getAllAgentIds(procurementSplits)]))
  },
)

/**
 * Get portfolio's primary tenant by portfolio ID
 */
export const getPrimaryTenantByPortfolioId = getTenantByPortfolioId

/**
 * Get a portfolio's invoice templates by portfolio ID
 */
export const getInvoiceTemplatesByPortfolioId = createSelector<RootState, (id: TMaybeId) => TInvoiceTemplate[]>(
  [getPortfolioById],
  (byId: (id: TMaybeId) => TPortfolio | undefined) => (id: TMaybeId) => byId(id)?.invoiceTemplates ?? [],
)

/**
 * Get a portfolio's commission by portfolio ID
 */
export const getCommissionByPortfolioId = createSelector<RootState, (id: TMaybeId) => TCommission>(
  [getPortfolioById],
  (byId: (id: TMaybeId) => TPortfolio | undefined) => (id: TMaybeId) => byId(id)?.commission ?? {},
)

/**
 * Finds a portfolio's invoice template by category
 */
export const getPortfolioInvoiceTemplateByCategory = createSelector<
  RootState,
  (id: TMaybeId, category: string) => TInvoiceTemplate | undefined
>(
  [getInvoiceTemplatesByPortfolioId],
  (byId: (id: TMaybeId) => TInvoiceTemplate[]) => (id: TMaybeId, category: string) =>
    byId(id).find(template => template.category === category),
)

const templateByCategory = (category: string) =>
  createSelector<RootState, (id: TMaybeId) => TInvoiceTemplate | undefined>(
    [getPortfolioInvoiceTemplateByCategory],
    (byCategory: (id: TMaybeId, category: string) => TInvoiceTemplate | undefined) => (id: TMaybeId) =>
      byCategory(id, category),
  )

export const getRentInvoiceTemplateByPortfolioId = templateByCategory('RentInvoice')
export const getLeaseFeeInvoiceTemplateByPortfolioId = templateByCategory('LeaseFee')
export const getApplicationFeeInvoiceTemplateByPortfolioId = templateByCategory('ApplicationFee')
export const getKeyDepositInvoiceTemplateByPortfolioId = templateByCategory('KeyDeposit')
export const getServiceDepositInvoiceTemplateByPortfolioId = templateByCategory('ServiceDeposit')
export const getDamageDepositInvoiceTemplateByPortfolioId = templateByCategory('DamageDeposit')

/**
 * Get a portfolio's rent invoice schedule
 */
export const getRentTemplateScheduleByPortfolioId = createSelector<
  RootState,
  (id: TMaybeId) => { sendAt: string; dueDate: string }
>([identity], (state: RootState) => (id: TMaybeId) => {
  const rentTemplateId = getRentInvoiceTemplateByPortfolioId(state)(id)?.id
  const rentTemplateSchedule = getInvoiceSchedulesByPortfolioId(state)(id).find(
    s => s.invoiceTemplateId === rentTemplateId,
  )?.dates?.[0]

  return {
    sendAt: rentTemplateSchedule?.sendAt ?? '',
    dueDate: rentTemplateSchedule?.dueDate ?? '',
  }
})

/**
 * Get a portfolio's invoice templates by scheduled invoices
 */
export const getInvoiceTemplatesByScheduledInvoices = createSelector<
  RootState,
  (id: TMaybeId) => (TInvoiceTemplate | undefined)[]
>([identity], (state: RootState) => (id: TMaybeId) => {
  const templates = getInvoiceTemplatesByPortfolioId(state)(id)
  return getInvoiceSchedulesByPortfolioId(state)(id).map(s => templates.find(t => s.invoiceTemplateId === t.id))
})

/**
 * Get portfolio status label for use in UI
 */
export const getPortfolioStatusLabel = createSelector<
  RootState,
  (id: TMaybeId, useSummary?: boolean) => TPortfolioStatus
>(
  [identity],
  (state: RootState) =>
    (id: TMaybeId, useSummary = false) =>
      (useSummary ? getSummaryById(state)(id)?.status : getPortfolioById(state)(id)?.status) ?? 'draft',
)

const isStatus = (expected: TPortfolioStatus) =>
  createSelector<RootState, (id: TMaybeId, useSummary?: boolean) => boolean>(
    [getPortfolioStatusLabel],
    (statusLabel: (id: TMaybeId, useSummary?: boolean) => TPortfolioStatus) =>
      (id: TMaybeId, useSummary = false) =>
        statusLabel(id, useSummary) === expected,
  )

export const isPortfolioExpired = isStatus('expired')
export const isPortfolioExpiring = isStatus('expiring')
export const isDraftPortfolio = isStatus('draft')
export const isActivePortfolio = isStatus('active')
export const isDeletedDraft = isStatus('deleted')
export const isPortfolioTerminated = isStatus('terminated')

export const getPortfolioStatusDescription = createSelector<RootState, (id: TMaybeId, useSummary?: boolean) => string>(
  [identity],
  (state: RootState) =>
    (id: TMaybeId, useSummary = false) => {
      const portfolio = useSummary ? getSummaryById(state)(id) : getPortfolioById(state)(id)
      const endDate = portfolio?.leaseTerms?.endDate

      /**
       * Deliberately always reads the *summary* status, even when the fields above came
       * from the portfolio: only summaries are loaded on the list screens this renders on.
       */
      switch (getPortfolioStatusLabel(state)(id, true)) {
        case 'expired':
          return `Expired ${dateUtils.timeAgo(new Date(endDate as string))}`
        case 'expiring':
          return `Expiring ${dateUtils.timeAgo(new Date(endDate as string))}`
        case 'renewal':
          return isValid(new Date(portfolio?.renewal?.leaseRenewsAt as string))
            ? `Renewing ${dateUtils.timeAgo(new Date(portfolio?.renewal?.leaseRenewsAt as string))}`
            : 'Renewing in ...'
        default:
          return ''
      }
    },
)

export const getTerminatedPortfolioVacateDate = createSelector<
  RootState,
  (id: TMaybeId, useSummary?: boolean) => string | undefined
>([identity], (state: RootState) => (id: TMaybeId, useSummary = false) => {
  const portfolio = useSummary ? getSummaryById(state)(id) : getPortfolioById(state)(id)

  if (getPortfolioStatusLabel(state)(id, true) === 'terminated') {
    return `Vacate date: ${dateUtils.formatStandard(new Date(portfolio?.terminationFields?.vacateDate as string))}`
  }
})

export const getTerminatedPortfolioTerminatedReason = createSelector<
  RootState,
  (id: TMaybeId, useSummary?: boolean) => string | undefined
>([identity], (state: RootState) => (id: TMaybeId, useSummary = false) => {
  const portfolio = useSummary ? getSummaryById(state)(id) : getPortfolioById(state)(id)

  if (getPortfolioStatusLabel(state)(id, true) === 'terminated') {
    const terminatedReason = portfolio?.terminationFields?.reason ?? ''
    return `Termination reason: ${terminatedReason.split(/(?=[A-Z])/).join(' ')}`
  }
})

export const getTerminatedPortfolioTerminatedDate = createSelector<
  RootState,
  (id: TMaybeId, useSummary?: boolean) => string | undefined
>([identity], (state: RootState) => (id: TMaybeId, useSummary = false) => {
  const portfolio = useSummary ? getSummaryById(state)(id) : getPortfolioById(state)(id)

  if (getPortfolioStatusLabel(state)(id, true) === 'terminated') {
    return `Termination date: ${dateUtils.formatStandard(
      new Date(portfolio?.terminationFields?.terminatedAt as string),
    )}`
  }
})

export const getPortfolioMetaData = createSelector<RootState, (id: TMaybeId, useSummary?: boolean) => string>(
  [identity],
  (state: RootState) =>
    (id: TMaybeId, useSummary = false) =>
      (useSummary ? getSummaryById(state)(id)?.metaData : getPortfolioById(state)(id)?.metaData) || '',
)

export const isPortfolioMonthToMonth = createSelector<RootState, (id: TMaybeId) => boolean>(
  [getPortfolioById],
  (byId: (id: TMaybeId) => TPortfolio | undefined) => (id: TMaybeId) => utils.isPortfolioMonthToMonth(byId(id)),
)

/**
 * Get a list of lease termination reasons
 */
export const getLeaseTerminationReasons = getSlice('terminationReasons')

/**
 * Get Bulk Invoices CSV Template base64 string
 */
export const getBulkInvoicesCsvTemplate = getSlice('bulkInvoicesCsvTemplate')

/**
 * ----
 * Summaries
 * ----
 */

/**
 * Get a list of portfolio summary objects
 */
export const getPortfolioSummaries = getSlice('summaries')

/**
 * Get a list of portfolio summaries filtered by currentSegments
 */
export const getPortfolioSummariesFilteredByCurrentSegments = createSelector<RootState, TPortfolioSummary[]>(
  [identity, getPortfolioSummaries],
  (state: RootState, summaries: TPortfolioSummary[]) => {
    const currentSegments: string[] = userApiSelectors.getCurrentSegments(state)
    return summaries.filter(summary => (summary.segments ?? []).some(segment => currentSegments.includes(segment)))
  },
)

/**
 * Get a portfolio summary object by ID
 */
export const getSummaryById = createSelector<RootState, (id: TMaybeId) => TPortfolioSummary | undefined>(
  [getPortfolioSummaries],
  (summaries: TPortfolioSummary[]) => (id: TMaybeId) => summaries.find(summary => summary.portfolioId === id),
)

/**
 * Portfolio summary statuses as a lookup. `getPortfolioStatusLabel` scans the whole summary list
 * on every call, which is fine for a screen reading one or two but not for the contacts list,
 * which reads one per account across every contact on every render.
 */
export const getSummaryStatusByPortfolioId = createSelector<RootState, (id: TMaybeId) => TPortfolioStatus>(
  [getPortfolioSummaries],
  (summaries: TPortfolioSummary[]) => {
    const statusById = new Map(summaries.map(summary => [summary.portfolioId, summary.status]))

    // Same fallback as `getPortfolioStatusLabel`: an id with no summary loaded reads as a draft.
    return (id: TMaybeId) => statusById.get(id as string) ?? 'draft'
  },
)

/**
 * Get a list of portfolio summaries with a specific property ID
 */
export const getSummariesByPropertyId = createSelector<RootState, (propertyId: TMaybeId) => TPortfolioSummary[]>(
  [getPortfolioSummaries],
  (summaries: TPortfolioSummary[]) => (propertyId: TMaybeId) =>
    summaries.filter(summary => summary.propertyId === propertyId),
)

/**
 * Read an arbitrary path off a portfolio summary.
 * EG: `getSummaryFieldById(state)(id, ['landlords', 0, 'partyId'])`
 *
 * See the note on {@link getPortfolioFieldById}.
 */
export const getSummaryFieldById = createSelector<RootState, TFieldReader>(
  [identity],
  (state: RootState) => (id: TMaybeId, path: (string | number)[], defaultValue?: unknown) =>
    pathOr(defaultValue, path, getSummaryById(state)(id)),
)

export const getSegmentIdsByPortfolioId = createSelector<RootState, (portfolioId: TMaybeId) => string[]>(
  [identity],
  (state: RootState) => (portfolioId: TMaybeId) => {
    const defaultSegmentId = userApiSelectors.getCurrentAgencyId(state)
    return (getSummaryById(state)(portfolioId)?.segments ?? []).filter(s => s !== defaultSegmentId)
  },
)

export const getStatuses = getSlice('status')

export const getIndependantStatusByPortfolioId = createSelector<RootState, (portfolioId: TMaybeId) => string>(
  [getStatuses],
  (statuses: TPortfolioState['status']) => (portfolioId: TMaybeId) =>
    statuses.find(status => status.portfolioId === portfolioId)?.status ?? '',
)
